// @file Surface post connection store
import { captureNonNetworkFetchError } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { ACCESS_WITHDRAWN } from '@@/bits/snackbar_helper'
import { vDel } from '@@/bits/vue'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { Id, PostConnection } from '@@/types'
import type { JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

type PostConnectionId = Id | string

export const useSurfacePostConnectionStore = defineStore('surfacePostConnection', () => {
  const surfaceStore = useSurfaceStore()
  const surfacePostsStore = useSurfacePostsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()

  /* ---------------------- */
  /* STATE                  */
  /* ---------------------- */

  const connectionsFetched = ref<boolean>(false)
  const connectionEntities = ref<Record<PostConnectionId, PostConnection>>({})
  const pendingConnectionIds = ref<string[]>([])
  const postToConnectFromId = ref<Id | null>(null)
  const postToDisconnectFromId = ref<Id | null>(null)
  const snackBarUid = ref<string | null>(null)
  // Session tracking state
  const connectionsCreatedInSession = ref<PostConnectionId[]>([])
  const MAX_TRACKED_SESSIONS = 10
  const sessionId = ref<number>(0)
  const sessionsFinishedByUser = ref<Record<number, boolean>>({})

  // Check if multiple connections mode is enabled
  const useMultipleConnections = isAppUsing('surfaceMultipleConnections')

  /* ---------------------- */
  /* GETTERS                */
  /* ---------------------- */

  const connections = computed<PostConnection[]>(() => {
    return connectionIds.value.map((id) => connectionEntities.value[id])
  })
  const currentConnections = computed<PostConnection[]>(() => {
    const currentPostIds: Id[] = surfacePostsStore.currentSortedPosts.flatMap((p) => (p.id != null ? [p.id] : []))
    return connections.value.filter(
      (c: PostConnection) => currentPostIds.includes(c.from_wish_id) && currentPostIds.includes(c.to_wish_id),
    )
  })
  const connectionIds = computed<PostConnectionId[]>(() => Object.keys(connectionEntities.value))

  const xConnectionCancellation = computed<boolean>((): boolean => postToConnectFromId.value != null)
  const xDisconnectionCancellation = computed<boolean>((): boolean => postToDisconnectFromId.value != null)

  /* ---------------------- */
  /* HELPER FUNCTIONS       */
  /* ---------------------- */

  const saveFetchedConnections = (connections: PostConnection[]): void => {
    if (connections != null) {
      const newConnectionEntities = {}
      connections.forEach((r) => {
        newConnectionEntities[r.id] = r
      })
      connectionEntities.value = newConnectionEntities // Trigger state change once
      pendingConnectionIds.value = []
    }
    connectionsFetched.value = true
  }

  const addPendingConnection = (pendingConnection: Partial<PostConnection>): string | undefined => {
    const tempId = 'new_' + String(new Date().valueOf())
    const newPendingConnection = {
      ...pendingConnection,
      id: tempId,
    }
    connectionEntities.value = {
      ...connectionEntities.value,
      // TODO: fix PostConnection type - pending connections use string IDs temporarily
      [tempId]: newPendingConnection as PostConnection & { id: string },
    }
    pendingConnectionIds.value.push(tempId)
    return useMultipleConnections ? tempId : undefined
  }

  const addConnection = (connection: PostConnection): void => {
    const pendingConnectionIdRemoved = pendingConnectionIds.value.some((tempId) => {
      if (
        connectionEntities.value[tempId].from_wish_id === connection.from_wish_id &&
        connectionEntities.value[tempId].to_wish_id === connection.to_wish_id &&
        connectionEntities.value[tempId].label === connection.label
      ) {
        const newConnectionEntities = { ...connectionEntities.value }
        // delete pending connection
        vDel(newConnectionEntities, tempId)
        // put new connection in place of pending connection
        connectionEntities.value = {
          ...newConnectionEntities,
          [connection.id]: connection,
        }
        pendingConnectionIds.value = pendingConnectionIds.value.filter((i) => i !== tempId)
        return true
      }
      return false
    })
    if (!pendingConnectionIdRemoved) {
      connectionEntities.value = {
        ...connectionEntities.value,
        [connection.id]: connection,
      }
    }
  }

  const removeConnection = (id: PostConnectionId): void => {
    const newConnectionEntities = { ...connectionEntities.value }
    vDel(newConnectionEntities, id)
    connectionEntities.value = newConnectionEntities
  }

  const removeConnections = (ids: PostConnectionId[]): void => {
    const idsToRemove = new Set(ids)
    const newConnectionEntities = Object.fromEntries(
      Object.entries(connectionEntities.value).filter(([id]) => !idsToRemove.has(id)),
    )
    connectionEntities.value = newConnectionEntities
  }

  const getConnectionsFromPost = (postId: Id): PostConnection[] => {
    return connections.value.filter((c: PostConnection) => c.from_wish_id === postId)
  }

  const getConnectionsToPost = (postId: Id): PostConnection[] => {
    return connections.value.filter((c: PostConnection) => c.to_wish_id === postId)
  }

  /* ---------------------- */
  /* ACTIONS                */
  /* ---------------------- */

  const fetchConnections = async (): Promise<void> => {
    try {
      const { PostConnectionApi } = await import('@@/surface/api/post_connection')
      const response = await PostConnectionApi.readAll({ wall: { id: surfaceStore.wallId } })
      fetchConnectionsSuccess({ fetchedData: response })
    } catch (e) {
      // Don't capture exception when it's 401, show a snackbar instead
      if (e.status === 401) {
        globalSnackbarStore.setSnackbar(ACCESS_WITHDRAWN)
      } else {
        captureNonNetworkFetchError(e, { source: 'fetchConnections' })
      }
    }
  }

  const fetchConnectionsSuccess = ({ fetchedData }: { fetchedData: JsonAPIResponse<PostConnection> }): void => {
    const connections = (fetchedData.data as Array<JsonAPIResource<PostConnection>>).map(
      (connection) => connection.attributes,
    )
    saveFetchedConnections(connections)
  }

  const cancelConnection = (): void => {
    if (useMultipleConnections) {
      // Remove all connections created during this session
      if (connectionsCreatedInSession.value.length > 0) {
        // Get all connections that exist (handles both pending string IDs and completed numeric IDs)
        const connectionsToDelete = connectionsCreatedInSession.value
          .map((id) => connectionEntities.value[id])
          .filter((conn) => conn != null)

        // Remove each connection
        connectionsToDelete.forEach((connection) => {
          // Only delete from server if it's not a pending connection (has numeric ID)
          if (typeof connection.id === 'number') {
            void import('@@/surface/api/post_connection').then(({ PostConnectionApi }) => {
              void PostConnectionApi.delete(connection)
            })
          }
          // Remove from local state
          removeConnection(connection.id)
        })

        connectionsCreatedInSession.value = []
      }
    }
    postToConnectFromId.value = null
  }

  const cancelDisconnection = (): void => {
    postToDisconnectFromId.value = null
  }

  const removeConnectionsForPost = ({ postId }: { postId: Id }): void => {
    const relatedConnections = connectionIds.value.filter((id) => {
      return connectionEntities.value[id].from_wish_id === postId || connectionEntities.value[id].to_wish_id === postId
    })
    removeConnections(relatedConnections)
  }

  const completeConnection = ({ postToConnectToId, label }: { postToConnectToId: Id; label: string }): void => {
    const newConnection = {
      from_wish_id: postToConnectFromId.value ?? undefined,
      to_wish_id: postToConnectToId,
      label,
    }

    if (useMultipleConnections) {
      // Track the pending connection ID
      const tempId = addPendingConnection(newConnection) as string
      connectionsCreatedInSession.value.push(tempId)
      // Capture session state at connection creation time to prevent race conditions
      const creationSessionId = sessionId.value
      void import('@@/surface/api/post_connection').then(({ PostConnectionApi }) => {
        void PostConnectionApi.create(newConnection)
          .then((connectionData: JsonAPIResponse<PostConnection>) => {
            const createdConnection = (connectionData.data as JsonAPIResource<PostConnection>).attributes
            // Only process if still in same session
            if (creationSessionId !== sessionId.value) {
              // Session changed - only delete if session wasn't finished by user
              const sessionWasFinishedByUser = sessionsFinishedByUser.value[creationSessionId] === true
              if (!sessionWasFinishedByUser) {
                // Session changed and wasn't finished by user, delete the orphaned connection from server
                void PostConnectionApi.delete(createdConnection)
                // Remove the pending connection from local state
                removeConnection(tempId)
                return
              }
              // Session changed but was finished by user
            }
            // Check if the session was canceled. A session is canceled if:
            // 1. Session wasn't finished by user
            // 2. AND (tempId is no longer tracked OR postToConnectFromId is null)
            const tempIndex = connectionsCreatedInSession.value.indexOf(tempId)
            const sessionWasFinishedByUser = sessionsFinishedByUser.value[creationSessionId] === true
            const sessionWasCanceled =
              !sessionWasFinishedByUser && (tempIndex === -1 || postToConnectFromId.value == null)

            if (sessionWasCanceled) {
              // Session was canceled, delete the created connection from server
              void PostConnectionApi.delete(createdConnection)
              // Remove the pending connection from local state if it still exists
              removeConnection(tempId)
              return
            }

            if (sessionWasFinishedByUser) {
              // Session finished by user, keep the connection
              // Remove tempId from tracking array
              if (tempIndex !== -1) {
                connectionsCreatedInSession.value.splice(tempIndex, 1)
              }
              addConnection(createdConnection)
            } else {
              // Replace the temp ID with the actual connection ID in tracking
              connectionsCreatedInSession.value[tempIndex] = createdConnection.id
              addConnection(createdConnection)
            }
          })
          .catch(() => {
            // If API call fails, tempId remains in connectionsCreatedInSession
            // It will be cleaned up when user cancels
          })
      })
    } else {
      postToConnectFromId.value = null
      addPendingConnection(newConnection)
      void import('@@/surface/api/post_connection').then(({ PostConnectionApi }) => {
        void PostConnectionApi.create(newConnection).then((connectionData: JsonAPIResponse<PostConnection>) => {
          addConnection((connectionData.data as JsonAPIResource<PostConnection>).attributes)
        })
      })
    }
  }

  const completeDisconnection = ({ connectionId }: { connectionId: Id }): void => {
    const existingConnection = connectionEntities.value[connectionId]
    postToDisconnectFromId.value = null
    if (existingConnection != null) {
      removeConnection(existingConnection.id)
      void import('@@/surface/api/post_connection').then(({ PostConnectionApi }) => {
        void PostConnectionApi.delete(existingConnection)
      })
    }
  }

  const newConnectionRemote = (connection: PostConnection): void => {
    addConnection(connection)
  }

  const deleteConnectionRemote = (connectionOrId: PostConnection | PostConnectionId): void => {
    const id = typeof connectionOrId === 'object' ? connectionOrId.id : connectionOrId
    removeConnection(id)
  }

  const finishConnection = (): void => {
    if (useMultipleConnections) {
      // Mark session as finished by user so pending API calls don't treat it as canceled
      sessionsFinishedByUser.value[sessionId.value] = true
      const failedConnectionIds = connectionsCreatedInSession.value.filter((id) => typeof id === 'string')
      if (failedConnectionIds.length > 0) {
        failedConnectionIds.forEach((tempId) => {
          removeConnection(tempId)
          pendingConnectionIds.value = pendingConnectionIds.value.filter((id) => id !== tempId)
        })
      }
      postToConnectFromId.value = null
    }
  }

  const startConnection = ({ postId }: { postId: Id }): void => {
    postToDisconnectFromId.value = null
    postToConnectFromId.value = postId
    if (useMultipleConnections) {
      // Initialize tracking for connections created in this session
      connectionsCreatedInSession.value = []
      sessionId.value++
      // Clean up old session entries
      if (sessionId.value > MAX_TRACKED_SESSIONS) {
        const oldSessionId = sessionId.value - MAX_TRACKED_SESSIONS
        delete sessionsFinishedByUser.value[oldSessionId]
      }
      if (snackBarUid.value == null) {
        snackBarUid.value = globalSnackbarStore.setSnackbar({
          message: 'Choose which posts to connect to',
          persist: true,
          actionText: 'Done',
          actionTextActions: [finishConnection],
        })
      }
    } else {
      if (snackBarUid.value == null) {
        snackBarUid.value = globalSnackbarStore.setSnackbar({
          message: 'Pick a post to connect to',
          persist: true,
          actionText: 'Cancel',
          actionTextActions: [cancelConnection],
        })
      }
    }
  }

  watch([xConnectionCancellation], () => {
    if (!xConnectionCancellation.value && snackBarUid.value != null) {
      globalSnackbarStore.removeSnackbar(snackBarUid.value)
      snackBarUid.value = null
    }
  })

  const startDisconnection = ({ postId }: { postId: Id }): void => {
    if (useMultipleConnections && postToConnectFromId.value != null) {
      // Mark the current session as finished before incrementing
      sessionsFinishedByUser.value[sessionId.value] = true
      sessionId.value++
      // Clean up old session entries
      if (sessionId.value > MAX_TRACKED_SESSIONS) {
        const oldSessionId = sessionId.value - MAX_TRACKED_SESSIONS
        delete sessionsFinishedByUser.value[oldSessionId]
      }
    }
    // Clear the connection snackbar before setting postToConnectFromId to null
    // Prevent the xConnectionCancellation watcher from removing the disconnection snackbar
    if (snackBarUid.value != null) {
      globalSnackbarStore.removeSnackbar(snackBarUid.value)
      snackBarUid.value = null
    }
    postToConnectFromId.value = null
    postToDisconnectFromId.value = postId

    snackBarUid.value = globalSnackbarStore.setSnackbar({
      message: 'Pick a connector to remove',
      persist: true,
      actionText: 'Cancel',
      actionTextActions: [cancelDisconnection],
    })
  }

  watch([xDisconnectionCancellation], () => {
    if (!xDisconnectionCancellation.value && snackBarUid.value != null) {
      globalSnackbarStore.removeSnackbar(snackBarUid.value)
      snackBarUid.value = null
    }
  })

  return {
    // Getters
    connections,
    currentConnections,
    postToConnectFromId,
    postToDisconnectFromId,
    xConnectionCancellation,
    xDisconnectionCancellation,

    // Actions
    fetchConnections,
    cancelConnection,
    cancelDisconnection,
    removeConnectionsForPost,
    completeConnection,
    completeDisconnection,
    newConnectionRemote,
    deleteConnectionRemote,
    startConnection,
    finishConnection,
    startDisconnection,
    getConnectionsFromPost,
    getConnectionsToPost,
  }
})
